import * as React from "react";
import { useQueryClient } from "react-query";
import { useMutateLogout } from "../data/services/authService";
import UserContext from "../layouts/UserContext";
import { AETNA_NOTICE_KEY_PREFIX } from "./aetnaRenewalNotice";
import {
  BROKER_LOGOUT_RETURN_PATH,
  clearBrokerLoggedOut,
  clearOktaAuthState,
  markBrokerLoggedOut,
  performBrokerOktaLogout,
} from "./oktaLogout";

export const LOGOUT_REQUEST_TIMEOUT_MS = 5000;
export const LOGIN_ROUTE = "/login";

const EXPECTED_LOGOUT_FAILURE_STATUSES = new Set([400, 401, 403]);

const timeoutLogoutRequest = (logoutPromise) =>
  Promise.race([
    logoutPromise,
    new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error("Logout request timed out.")),
        LOGOUT_REQUEST_TIMEOUT_MS
      );
    }),
  ]);

// Storage keys by index rather than Object.keys, which relies on the named
// property getter and would also surface Storage members if one ever collided
// with a stored key.
const storageKeys = (storage) => {
  const keys = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);

    if (key !== null) {
      keys.push(key);
    }
  }

  return keys;
};

/**
 * Wipes a Storage, optionally keeping keys under the given prefixes.
 *
 * The preserve list exists for one-time notices whose "already seen" flag must
 * outlive a logout (see utils/aetnaRenewalNotice.js). Those keys are namespaced
 * by user id, so keeping them does not leak state to the next person to log in
 * on the same machine. sessionStorage is still cleared wholesale - auth state
 * must never survive logout.
 */
const clearBrowserStorage = (storage, preservePrefixes = []) => {
  try {
    if (!storage) {
      return;
    }

    const preserved =
      preservePrefixes.length > 0
        ? storageKeys(storage)
            .filter((key) =>
              preservePrefixes.some((prefix) => key.startsWith(prefix))
            )
            .map((key) => [key, storage.getItem(key)])
        : [];

    storage.clear();

    preserved.forEach(([key, value]) => {
      if (value !== null) {
        storage.setItem(key, value);
      }
    });
  } catch (_) {
    // storage access denied (e.g. private browsing restrictions)
  }
};

export const getPostLogoutRoute = () => BROKER_LOGOUT_RETURN_PATH;

export const useLogoutHandler = () => {
  const queryClient = useQueryClient();
  const { setLoggedInUser } = React.useContext(UserContext);
  const { mutateAsync } = useMutateLogout();

  const clearClientState = React.useCallback(
    ({ preserveBrokerMarker = false } = {}) => {
      setLoggedInUser(null);
      queryClient.clear();
      clearBrowserStorage(window.localStorage, [AETNA_NOTICE_KEY_PREFIX]);
      clearOktaAuthState();

      clearBrowserStorage(window.sessionStorage);

      if (preserveBrokerMarker) {
        markBrokerLoggedOut();
      } else {
        clearBrokerLoggedOut();
      }
    },
    [queryClient, setLoggedInUser]
  );

  const redirectToLogin = React.useCallback(() => {
    window.location.replace(getPostLogoutRoute());
  }, []);

  const logout = React.useCallback(async () => {
    try {
      const response = await timeoutLogoutRequest(mutateAsync());

      if (response?.data?.idToken) {
        clearClientState({ preserveBrokerMarker: true });
        await performBrokerOktaLogout(response.data.idToken);
        return;
      }
    } catch (error) {
      const statusCode = error?.response?.status;

      if (!EXPECTED_LOGOUT_FAILURE_STATUSES.has(statusCode)) {
        console.warn(
          "[useLogoutHandler] Unexpected logout error:",
          error?.message ?? String(error)
        );
      }
    }

    clearClientState();
    redirectToLogin();
  }, [clearClientState, mutateAsync, redirectToLogin]);

  return { logout };
};
